import * as React from 'react';
import { createPortal } from 'react-dom';
import { css, StyleSheet } from 'aphrodite';
import * as registeredBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock.json';
import * as dailyLessonBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock_daily_lesson.json';
import * as favoriteBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock_favorite.json';
import * as mealBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock_meal.json';
import * as stepsBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock_steps.json';
import * as weightBadgeAnimation from '../../lotties/first_mission/leanbody_badge_unlock_weight.json';
import * as completedStampAnimation from '../../lotties/first_mission/leanbody_completed_stamp.json';
import { Button, ButtonType } from '../atoms/button';
import { ArrowIconWithMargin } from '../atoms/icons/arrow_icon_with_margin';
import { ArrowDirection } from '../atoms/icons/arrow_icon';
import { LottieIcon } from '../atoms/icons/lottie_icon';
import { MissionBadge } from '../molecules/mission_badge';
import { Colors, MediaBreakPointUp } from '../styles/variables';
import { Overlay } from './overlay';
const achievementAnimations = {
    registered: registeredBadgeAnimation,
    daily_lesson_completed: dailyLessonBadgeAnimation,
    lesson_favorited: favoriteBadgeAnimation,
    weight_recorded: weightBadgeAnimation,
    meal_recorded: mealBadgeAnimation,
    step_goal_achieved: stepsBadgeAnimation,
};
const COMPLETED_STAMP_ANIMATION_DELAY_MS = 1000;
export const BeginnerMissionModal = ({ mission, onClose }) => {
    const items = mission.items;
    const isCompleted = mission.isCompleted;
    const [isCompletedStampPaused, setIsCompletedStampPaused] = React.useState(true);
    const missionBadges = items.map((item) => ({
        item,
        achievementAnimationData: item.code ? achievementAnimations[item.code] : undefined,
    }));
    React.useEffect(() => {
        if (!isCompleted)
            return;
        const timeoutId = window.setTimeout(() => {
            setIsCompletedStampPaused(false);
        }, COMPLETED_STAMP_ANIMATION_DELAY_MS);
        return () => window.clearTimeout(timeoutId);
    }, [isCompleted]);
    return createPortal(React.createElement(Overlay, { background: "transparent" },
        React.createElement("div", { className: css(styles.modal), role: "dialog", "aria-modal": "true", "aria-label": "\u30D3\u30AE\u30CA\u30FC\u30DF\u30C3\u30B7\u30E7\u30F3" },
            React.createElement("main", { className: css(styles.page) },
                React.createElement("div", { className: css(styles.mobileHeader) },
                    React.createElement("img", { src: "/assets/images/logo-black.svg", alt: "LEAN BODY", className: css(styles.logo) })),
                React.createElement("button", { type: "button", onClick: onClose, className: css(styles.back), "aria-label": "\u9589\u3058\u308B" },
                    React.createElement(ArrowIconWithMargin, { direction: ArrowDirection.left, color: Colors.TextDark })),
                React.createElement("section", { className: css(styles.center), "aria-labelledby": "beginner-mission-title" },
                    React.createElement("div", { className: css(styles.headline) },
                        React.createElement("p", { className: css(styles.eyebrow) }, "\u3053\u308C\u3067LEAN BODY\u30DE\u30B9\u30BF\u30FC!"),
                        React.createElement("h1", { id: "beginner-mission-title", className: css(styles.title) }, "\u30D3\u30AE\u30CA\u30FC\u30DF\u30C3\u30B7\u30E7\u30F3")),
                    React.createElement("div", { className: css(styles.periodGroup) },
                        React.createElement("img", { src: "/assets/images/first_mission/beginner_mission_sparkle.png", alt: "", className: css(styles.sparkle) }),
                        React.createElement("div", { className: css(styles.periodCenter) },
                            React.createElement("p", { className: css(styles.periodHint) }, "\uD83D\uDD30 \u671F\u9593\u4E2D\u306B\u9054\u6210\u3057\u3088\u3046\uFF01\uD83D\uDD30"),
                            React.createElement("p", { className: css(styles.period) }, mission.periodLabel)),
                        React.createElement("img", { src: "/assets/images/first_mission/beginner_mission_sparkle.png", alt: "", className: css(styles.sparkle, styles.sparkleRight) })),
                    React.createElement("div", { className: css(styles.missionCard, isCompleted && styles.completedMissionCard) },
                        items.length === 0 && React.createElement("p", null, "\u30DF\u30C3\u30B7\u30E7\u30F3\u3092\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002\u6642\u9593\u3092\u304A\u3044\u3066\u518D\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002"),
                        missionBadges.map(({ item, achievementAnimationData }) => (React.createElement(MissionBadge, { key: item.id, item: item, achievementAnimationData: achievementAnimationData }))),
                        isCompleted && (React.createElement(LottieIcon, { lottieData: completedStampAnimation, classObject: styles.completedStamp, isPaused: isCompletedStampPaused, loop: false })))),
                React.createElement("div", { className: css(styles.footer) },
                    React.createElement(Button, { buttonType: ButtonType.primary, className: css(styles.continueButton), onClick: onClose }, "\u4ECA\u65E5\u3082\u304C\u3093\u3070\u308B\uFF01"))))), document.body);
};
const styles = StyleSheet.create({
    modal: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
    },
    page: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100dvh',
        overflow: 'hidden',
        backgroundImage: 'repeating-conic-gradient(from -7.5deg at 50% 40%, rgba(255, 255, 255, 0.52) 0deg 7.5deg, transparent 7.5deg 15deg), linear-gradient(180deg, #FFF188 0%, #FFE46C 100%)',
        [MediaBreakPointUp.MD]: {
            justifyContent: 'center',
        },
    },
    mobileHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 50,
        backgroundColor: Colors.White,
        [MediaBreakPointUp.MD]: {
            display: 'none',
        },
    },
    logo: {
        width: 142,
        height: 24,
    },
    back: {
        position: 'absolute',
        top: 62,
        left: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        padding: 0,
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        [MediaBreakPointUp.MD]: {
            top: 16,
            left: 16,
        },
    },
    center: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        maxWidth: 440,
        marginTop: 'auto',
        marginBottom: 'auto',
        paddingTop: 70,
        paddingRight: 10,
        paddingBottom: 30,
        paddingLeft: 10,
        [MediaBreakPointUp.MD]: {
            paddingTop: 40,
            paddingBottom: 40,
        },
    },
    headline: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
        color: Colors.Orange,
        WebkitTextStroke: `4px ${Colors.White}`,
        paintOrder: 'stroke fill',
    },
    eyebrow: {
        margin: 0,
        fontSize: 20,
        fontWeight: 900,
        lineHeight: 1.3,
        letterSpacing: 0.96,
    },
    title: {
        margin: 0,
        fontSize: 'clamp(28px, 9vw, 34px)',
        fontWeight: 900,
        lineHeight: 1.3,
        letterSpacing: 0.96,
    },
    periodGroup: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
    },
    sparkle: {
        display: 'block',
        width: 52,
        height: 53,
        objectFit: 'contain',
        flexShrink: 1,
        minWidth: 35,
    },
    sparkleRight: {
        transform: 'rotate(90deg)',
    },
    periodCenter: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
    },
    periodHint: {
        margin: 0,
        fontSize: 15,
        fontWeight: 'bold',
        lineHeight: 1.3,
        letterSpacing: 0.6,
        whiteSpace: 'nowrap',
        color: Colors.TextDark,
        WebkitTextStroke: `2px ${Colors.White}`,
        paintOrder: 'stroke fill',
    },
    period: {
        margin: 0,
        padding: '4px 16px',
        borderRadius: 30,
        backgroundColor: '#FFBF27',
        color: Colors.White,
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 1.4,
        letterSpacing: 0.64,
        whiteSpace: 'nowrap',
    },
    missionCard: {
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        justifyItems: 'center',
        rowGap: 4,
        width: '100%',
        padding: '18px 15px',
        border: '5px solid #FFBF27',
        borderRadius: 20,
        backgroundColor: '#FFFCDE',
        boxShadow: '0 4px 2px rgba(0, 0, 0, 0.1)',
        [MediaBreakPointUp.MD]: {
            padding: '19px 25px',
        },
    },
    completedMissionCard: {
        marginBottom: 74,
    },
    completedStamp: {
        position: 'absolute',
        bottom: -74,
        left: 8,
        display: 'block',
        width: 'min(180px, 50%)',
        aspectRatio: '1 / 1',
        height: 'auto',
        pointerEvents: 'none',
        [MediaBreakPointUp.MD]: {
            left: 16,
        },
    },
    footer: {
        width: '100%',
        maxWidth: 440,
        padding: '0 16px 16px',
        [MediaBreakPointUp.MD]: {
            paddingBottom: 30,
        },
    },
    continueButton: {
        display: 'block',
        width: '100%',
        height: 48,
        padding: 0,
        border: 0,
        borderRadius: 30,
        background: Colors.ProgressGreen,
        color: Colors.White,
        fontSize: 16,
        fontWeight: 'bold',
        letterSpacing: 1.28,
        ':hover': {
            background: Colors.ProgressGreen,
        },
    },
});
